import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  SummaryCard,
  DocumentTable,
  UploadDialog,
  EditDialog,
  DeleteDialog,
} from './sub-modules/DocumentManagement';
import { DocumentListItem, DocumentUploadRequest } from '../../../types';
import { apiService } from '../../../services/api';

const DocumentManagement: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentListItem | null>(null);
  
  // Upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [availablePages, setAvailablePages] = useState<any[]>([]);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Load documents
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const documentsData = await apiService.getDocuments();
      setDocuments(documentsData);
      console.log('Documents loaded successfully:', documentsData);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load available pages (permissions)
  const loadAvailablePages = useCallback(async () => {
    try {
      const permissions = await apiService.getPermissions();
      setAvailablePages(permissions);
    } catch (err) {
      console.error('Error loading permissions:', err);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
    loadAvailablePages();
  }, [loadDocuments, loadAvailablePages]);

  // Show snackbar
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Format file size
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Format date
  const formatDate = useCallback((dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        showSnackbar('Only PDF and DOCX files are allowed', 'error');
        return;
      }
      setSelectedFile(file);
      setFileName(file.name);
    }
  }, [showSnackbar]);

  // Handle upload
  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      showSnackbar('Please select a file', 'error');
      return;
    }

    try {
      setUploading(true);
      await apiService.uploadDocument({
        file: selectedFile,
        fileName: fileName || selectedFile.name,
        sharedWithPages: selectedPages,
      });
      
      showSnackbar('Document uploaded successfully');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setFileName('');
      setSelectedPages([]);
      loadDocuments();
    } catch (err) {
      console.error('Error uploading document:', err);
      showSnackbar('Failed to upload document', 'error');
    } finally {
      setUploading(false);
    }
  }, [selectedFile, fileName, selectedPages, showSnackbar, loadDocuments]);

  // Handle edit
  const handleEdit = useCallback(async () => {
    if (!selectedDocument) return;

    try {
      setUploading(true);
      await apiService.updateDocument(selectedDocument.id, {
        fileName: fileName,
        sharedWithPages: selectedPages,
        ...(selectedFile && { file: selectedFile }),
      });
      
      showSnackbar('Document updated successfully');
      setEditDialogOpen(false);
      setSelectedDocument(null);
      setSelectedFile(null);
      setFileName('');
      setSelectedPages([]);
      loadDocuments();
    } catch (err) {
      console.error('Error updating document:', err);
      showSnackbar('Failed to update document', 'error');
    } finally {
      setUploading(false);
    }
  }, [selectedDocument, fileName, selectedFile, selectedPages, showSnackbar, loadDocuments]);

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!selectedDocument) return;

    try {
      await apiService.deleteDocument(selectedDocument.id);
      showSnackbar('Document deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
      loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      showSnackbar('Failed to delete document', 'error');
    }
  }, [selectedDocument, showSnackbar, loadDocuments]);

  // Handle download
  const handleDownload = useCallback(async (document: DocumentListItem) => {
    try {
      const blob = await apiService.downloadDocument(document.fileID);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
      showSnackbar('Failed to download document', 'error');
    }
  }, [showSnackbar]);

  // Open upload dialog
  const openUploadDialog = useCallback(() => {
    setSelectedFile(null);
    setFileName('');
    setUploadDialogOpen(true);
  }, []);

  // Open edit dialog
  const openEditDialog = useCallback((document: DocumentListItem) => {
    setSelectedDocument(document);
    setFileName(document.fileName);
    setSelectedFile(null);
    // Parse the shared pages from the document
    const sharedPages = document.sharedWithPages ? document.sharedWithPages.split(',').map(s => s.trim()).filter(s => s) : [];
    setSelectedPages(sharedPages);
    setEditDialogOpen(true);
  }, []);

  // Open delete dialog
  const openDeleteDialog = useCallback((document: DocumentListItem) => {
    setSelectedDocument(document);
    setDeleteDialogOpen(true);
  }, []);


  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading documents...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <SummaryCard documents={[]} />
        
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Typography variant="body2" color="text.secondary">
            You can still upload documents using the button above. The error might be due to missing database table or permissions.
          </Typography>
        </Box>
        
        <UploadDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          selectedFile={selectedFile}
          fileName={fileName}
          setFileName={setFileName}
          handleFileSelect={handleFileSelect}
          handleUpload={handleUpload}
          uploading={uploading}
          formatFileSize={formatFileSize}
          selectedPages={selectedPages}
          setSelectedPages={setSelectedPages}
          availablePages={availablePages}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box>
      <SummaryCard documents={documents} />
      
      <DocumentTable
        documents={documents}
        formatFileSize={formatFileSize}
        formatDate={formatDate}
        handleDownload={handleDownload}
        openUploadDialog={openUploadDialog}
        openEditDialog={openEditDialog}
        openDeleteDialog={openDeleteDialog}
      />

      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        selectedFile={selectedFile}
        fileName={fileName}
        setFileName={setFileName}
        handleFileSelect={handleFileSelect}
        handleUpload={handleUpload}
        uploading={uploading}
        formatFileSize={formatFileSize}
        selectedPages={selectedPages}
        setSelectedPages={setSelectedPages}
        availablePages={availablePages}
      />

      <EditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        selectedFile={selectedFile}
        fileName={fileName}
        setFileName={setFileName}
        handleFileSelect={handleFileSelect}
        handleEdit={handleEdit}
        uploading={uploading}
        selectedPages={selectedPages}
        setSelectedPages={setSelectedPages}
        availablePages={availablePages}
        selectedDocument={selectedDocument}
      />

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        handleDelete={handleDelete}
        selectedDocument={selectedDocument}
      />


      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentManagement;
